/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Typography, Divider } from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import {
  postRenderAuthorizationEmailTemplate,
  postSendRenderAuthorizationEmailTemplate
} from 'modules/emailTemplates/store/emailTemplate.actions';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { EMAIL_TEMPLATE_STATUS_IDS, TEMPLATE_USER_TYPES_IDS, ZERO_GUID } from 'modules/enumerations/enumerationIds';
import AuthServiceEmailComponents from './authServiceEmail.components';

const AuthServiceGlobalEmailCard = props => {
  const {
    authId,
    communication,
    serviceTemplates,
    serviceId,
    authSentTemplates,
    authCommentServicesEnum,
    authCommentRegardingEnum,
    history,
    selectedAuthServiceId,
    allVendorOptions,
    vendorOptions,
    selectedSegmentServiceId,
    emailTemplates,
    selectedVendorId = null,
    isReadOnly
  } = props;

  const getParentId = template => {
    if (template.parentEmailTemplateId && template.parentEmailTemplateId !== 0) {
      return template.parentEmailTemplateId;
    }
    return template.templateId;
  };

  const segmentLevelTemplateShouldShow = (template, sentTemplate) => {
    if (!template) return false;

    if (!template.selectedSegmentServiceId) {
      return (
        (sentTemplate.authServiceId === selectedAuthServiceId && sentTemplate.segmentId === selectedSegmentServiceId) ||
        sentTemplate.authServiceId === 0
      );
    } else {
      return sentTemplate.authServiceId === selectedAuthServiceId && sentTemplate.segmentId === template.selectedSegmentServiceId;
    }
  };

  const serviceRelatedTemplatesTransformed = useMemo(() => {
    if (!Array.isArray(serviceTemplates)) return [];

    const templatesByParentId = {};
    emailTemplates.forEach(t => {
      const parentId = getParentId(t);
      if (!templatesByParentId[parentId]) {
        templatesByParentId[parentId] = [];
      }
      templatesByParentId[parentId].push(t);
    });

    return serviceTemplates.map(t => {
      const parentId = getParentId(t);
      const versionFamilyIds = templatesByParentId[parentId]?.map(vt => vt.templateId) || [t.templateId];

      const sentTemplatesForFamily = Array.isArray(authSentTemplates)
        ? authSentTemplates.filter(st => versionFamilyIds.includes(st.templateId) && segmentLevelTemplateShouldShow(t, st))
        : [];

      if (!sentTemplatesForFamily.length) {
        return {
          ...t,
          hasBeenSent: false,
          sentDate: null,
          commentId: null,
          sentCount: 0,
          sentFromVersion: null
        };
      }
      const mostRecent = sentTemplatesForFamily.reduce((prev, current) =>
        new Date(prev.emailTemplateSentDate) > new Date(current.emailTemplateSentDate) ? prev : current
      );
      let sentFromTemplate = serviceTemplates.find(st => st.templateId === mostRecent.templateId);

      if (t.selectedSegmentServiceId) {
        sentFromTemplate = serviceTemplates.find(
          st => st.templateId === mostRecent.templateId && st.segmentId === t.selectedSegmentServiceId
        );
      }
      return {
        ...t,
        hasBeenSent: true,
        sentDate: mostRecent.emailTemplateSentDate,
        commentId: mostRecent.commentId,
        sentCount: sentTemplatesForFamily.length,
        sentFromVersion: sentFromTemplate?.templateName || null
      };
    });
  }, [serviceTemplates, serviceId, authSentTemplates, selectedAuthServiceId, selectedSegmentServiceId, emailTemplates]);

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const templatesToShow = serviceRelatedTemplatesTransformed;

  const onUnselectTemplate = () => {
    setSelectedTemplateId(null);
    setSelectedTemplate(null);
  };

  const onSelectTemplate = (templateId, selectedSegmentServiceId) => {
    let template;
    if (selectedSegmentServiceId) {
      template = serviceTemplates.find(t => t.templateId === templateId && t.selectedSegmentServiceId === selectedSegmentServiceId);
    } else {
      template = serviceTemplates.find(t => t.templateId === templateId);
    }

    setSelectedTemplate({...template, selectedVendorId: template?.selectedVendorId || selectedVendorId});
    setSelectedTemplateId(templateId);
  };

  const formatSentDate = sentDate => {
    if (!sentDate) return 'Sent Date';
    const d = new Date(sentDate);
    if (Number.isNaN(d.getTime())) return 'Sent Date';
    return d.toLocaleString('en-US');
  };

  const handleTemplateClick = commentId => {
    if (!commentId) return;
    history.push(`/authorizations/${authId}/comments?commentId=${commentId}`);
  };

  const getSendButtonLabel = template => {
    const typeId = template.templateUserTypeId;

    if (!typeId || isIdEqual(typeId, ZERO_GUID) || isIdEqual(typeId, TEMPLATE_USER_TYPES_IDS.TRANSFEREE)) {
      return 'Send to EE';
    }

    if (isIdEqual(typeId, TEMPLATE_USER_TYPES_IDS.VENDOR)) {
      return 'Send to V';
    }

    return 'Send Email';
  };

  return (
    <>
      <Card sx={{ width: '100%', mb: 2 }}>
        <CardContent sx={{ pb: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontSize: '1.3125rem',
                fontWeight: 'bold'
              }}
            >
              Service Actions
            </Typography>
            {/* {(isIdEqual(serviceId, SERVICE_IDS.TEMP_LIVING_SERVICE_ID) || isIdEqual(serviceId, SERVICE_IDS.HHG_SERVICE_ID)) && (
              <FormControlLabel
                control={
                  <Switch checked={!!viewGeneralTemplate} onChange={e => setViewGeneralTemplate(!viewGeneralTemplate)} color="primary" />
                }
                label="Service General Template"
              />
            )} */}
          </Box>

          {templatesToShow.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No email templates are configured for this service.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {templatesToShow
                .filter(s => isIdEqual(s.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.ACTIVE))
                .map((t, idx) => (
                  <React.Fragment key={t.templateId}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'baseline',
                          gap: 2,
                          pr: 2,
                          minWidth: 0
                        }}
                      >
                        {t.hasBeenSent && t.commentId ? (
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 500,
                              mb: 0.25,
                              textDecoration: 'underline',
                              color: '#0077cc',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                            onClick={() => handleTemplateClick(t.commentId)}
                          >
                            {t.displayName ? `${t.templateName} - ${t.displayName}` : t.templateName}
                          </Typography>
                        ) : (
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 500,
                              mb: 0.25,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {t.displayName ? `${t.templateName} - ${t.displayName}` : t.templateName}
                          </Typography>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                          {t.isSingleRequest && t.sentDate && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'success.main',
                                fontWeight: 600,
                                textTransform: 'uppercase'
                              }}
                            >
                              Done
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {formatSentDate(t.sentDate)}
                          </Typography>
                          {t.sentCount > 1 && (
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              ({t.sentCount} sent)
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={isReadOnly}
                        sx={{
                          backgroundColor: '#868686',
                          '&:hover': { backgroundColor: '#474747' },
                          minWidth: '120px'
                        }}
                        onClick={() => onSelectTemplate(t.templateId, t?.selectedSegmentServiceId)}
                      >
                        {getSendButtonLabel(t)}
                      </Button>
                    </Box>
                    {idx !== templatesToShow.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
            </Box>
          )}
        </CardContent>
      </Card>
      {selectedTemplateId && (
        <AuthServiceEmailComponents
          id={authId}
          communication={communication}
          emailTemplates={
            isIdEqual(selectedTemplate.templateUserTypeId, TEMPLATE_USER_TYPES_IDS.VENDOR)
              ? serviceTemplates.filter(
                  t =>
                    isIdEqual(t.templateUserTypeId, TEMPLATE_USER_TYPES_IDS.VENDOR) &&
                    isIdEqual(t.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.ACTIVE)
                )
              : serviceTemplates.filter(
                  t =>
                    !isIdEqual(t.templateUserTypeId, TEMPLATE_USER_TYPES_IDS.TRANSFEREE) &&
                    isIdEqual(t.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.ACTIVE)
                )
          }
          defaultServiceId={serviceId}
          authCommentServicesEnum={authCommentServicesEnum}
          authCommentRegardingEnum={authCommentRegardingEnum}
          selectedTemplateProp={selectedTemplateId}
          openModalDefault={true}
          onUnselectTemplate={onUnselectTemplate}
          selectedAuthServiceId={selectedAuthServiceId}
          // isVendorTemplate={isIdEqual(selectedTemplate.templateUserTypeId, TEMPLATE_USER_TYPES_IDS.VENDOR)}
          vendorOptions={vendorOptions}
          allVendorOptions={allVendorOptions}
          selectedSegmentServiceId={selectedTemplate.selectedSegmentServiceId ?? selectedSegmentServiceId}
          selectedVendorId={selectedTemplate.selectedVendorId ?? selectedVendorId}
        />
      )}
    </>
  );
};

const mapStateToProps = state => ({});

export default compose(
  connect(mapStateToProps, {
    showToast,
    postRenderAuthorizationEmailTemplate,
    postSendRenderAuthorizationEmailTemplate
  })
)(AuthServiceGlobalEmailCard);
